/**
 * Client-side validation for the email change: deliberately thin, like
 * `password-change.validation.ts`.
 *
 * Three rules can be decided here: the password is present, the address looks like
 * an address, and it is not the one already in force. The fourth (is it already
 * used by ANOTHER account) needs to look at rows this side cannot see, so it comes
 * back as a 409 and is placed on the address field by `email-change-error.ts`.
 * Validating here spares a round-trip for the cases we can be sure of; it does not
 * reimplement the policy.
 *
 * The regex is not written again: `@/utils/validation` is the app-wide check, the
 * same one the identity form uses. Its backend counterpart is `EmailIdentifier` in
 * the auth domain, which trims the same way.
 */
import { isValidEmail } from '@/utils/validation';

import type { EmailChangeDraft, EmailChangeValidationErrors } from './email-change.model';

/**
 * Validate the draft. `currentEmail` is what makes the "same address" rule possible;
 * the section reads it from `AuthContext`. Passing an empty string simply skips
 * that rule, and the backend still answers 400 for it.
 *
 * The password is only checked for presence: its correctness is the server's answer
 * (a 401), and guessing at it here would only teach the form to be wrong.
 */
export function validateDraft(
  draft: EmailChangeDraft,
  currentEmail = '',
): EmailChangeValidationErrors {
  const errors: EmailChangeValidationErrors = {};

  if (draft.currentPassword.length === 0) {
    errors.currentPassword = 'Enter your current password.';
  }

  const newEmail = draft.newEmail.trim();
  if (!newEmail) {
    errors.newEmail = 'Enter the new email address.';
  } else if (!isValidEmail(newEmail)) {
    errors.newEmail = 'Please enter a valid email address.';
  } else if (currentEmail && newEmail.toLowerCase() === currentEmail.trim().toLowerCase()) {
    // Compared case-insensitively HERE, while the backend compares verbatim. The
    // asymmetry is deliberate: `users.email` is matched exactly on login, so only
    // the server may decide two addresses are the same row. This side is just
    // catching the obvious mistake (retyping your own address) early.
    errors.newEmail = 'This is already your sign-in email.';
  }

  return errors;
}

/** Is the draft submittable? Cheap enough to call on every keystroke. */
export function isDraftValid(draft: EmailChangeDraft, currentEmail = ''): boolean {
  return Object.keys(validateDraft(draft, currentEmail)).length === 0;
}
